import React, { useState, useEffect } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { API_URL_ADMIN, PUBLIC_URL } from './../../constants'
import { Auth } from 'aws-amplify'
import Loader from './../loader/Loader'
import PasswordStrengthBar from 'react-password-strength-bar'
import './../login/Login.css'
import TermsModal from './TermsModal'
import { useCookies } from 'react-cookie'
import InputMask from 'react-input-mask'
import { useAuth } from '../../auth/Auth'
import { usePassword } from '../../auth/PasswordContext'
import { useLandAfterSignIn } from '../../auth/useLandAfterSignIn'
import Token from './../../apis/Token'
import {
    formatDateUnix,
    isValidDateUnix,
} from './../../utilities/generalUtils'
import { GpAuthShell, GpButton, GpFormField, Icon } from '../../components/gp'

const iconBtn = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--gp-ink-mute)',
    display: 'inline-flex',
    padding: 4,
}

const linkStyle = {
    color: 'var(--gp-brand)',
    textDecoration: 'none',
    fontWeight: 500,
    cursor: 'pointer',
}

const SignUp = (props) => {
    const [accountExists, setAccountExists] = useState(false)
    const [passwordNotSafe, setPasswordNotSafe] = useState(false)
    const [email, setEmail] = useState(props.email ?? '')
    const [name, setName] = useState('')
    const [lastName, setLastName] = useState('')
    const [password, setPassword] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [disabledEmail] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [termsLoading, setTermsLoading] = useState(false)
    const [waitingForCode, setWaitingForCode] = useState(false)
    const [invalidCode, setInvalidCode] = useState(false)
    const [openTermsModal, setOpenTermsModal] = useState(false)
    const [cookies, setCookie, removeCookie] = useCookies(['gopowerev', 'inviteUuid', 'inviteRole'])
    const [showInviteError, setShowInviteError] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [invalidEmail, setInvalidEmail] = useState(false)
    const [invalidPhone, setInvalidPhone] = useState(false)
    const [role, setRole] = useState('CONSUMER')
    const [privacyUUID, setPrivacyUUID] = useState(null)
    const [termsOfServiceUUID, setTermsOfServiceUUID] = useState(null)
    const [redirectUrl, setRedirectUrl] = useState('')
    const [inviteExpired, setInviteExpired] = useState(false)
    const [termsAccepted, setTermsAccepted] = useState(false)
    const [termsText, setTermsText] = useState(undefined)
    const [privacyText, setPrivacyText] = useState(undefined)
    const [inviteChecked, setInviteChecked] = useState(false)
    const [isGuestCharging, setIsGuestCharging] = useState(props.isGuestCharging)
    const [guestExpiration, setGuestExpiration] = useState()
    const [code, setCode] = useState('')

    let { pathId } = useParams()
    const history = useHistory()
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const expiration = queryParams.get('expiration')
    const auth = useAuth()
    const landAfterSignIn = useLandAfterSignIn()
    const { password: contextPassword, clearPassword } = usePassword()

    const goToLogin = () => history.push('/sign-in')

    const clearErrors = () => {
        setAccountExists(false)
        setPasswordNotSafe(false)
    }

    const makeTermsApiCall = (pUUID, tUUID) => {
        const post = (uuid) =>
            fetch(API_URL_ADMIN + 'api/user/legal/agreement', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + Token.getToken(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ legalDocumentUUID: uuid }),
            })
                .then((r) => r.json())
                .catch(() => {})
        post(pUUID)
        post(tUUID)
    }

    const logIn = async () => {
        setIsLoading(true)
        const passwordToUse = contextPassword || password
        auth.signin(email, passwordToUse, {})
            .then(async (user) => {
                document.querySelector('body').scrollTo(0, 0)
                setEmail('')
                setPassword('')
                clearPassword()
                makeTermsApiCall(privacyUUID, termsOfServiceUUID)
                if (auth.token && privacyUUID && termsOfServiceUUID) {
                    makeTermsApiCall(privacyUUID, termsOfServiceUUID)
                }
                // A new account invited as an admin or manager holds only that role, so
                // this sends it to its own app like any sign-in here.
                landAfterSignIn(user)
                // The invite cookie stays for a guest's later sign-ins; its role is spent.
                removeCookie('inviteRole', { path: '/' })
            })
            .catch(() => {})
            .finally(() => setIsLoading(false))
    }

    const resendCode = () => {
        document.querySelector('body').scrollTo(0, 0)
        Auth.resendSignUp(email)
            .then(() => alert('Code resent successfully'))
            .catch(() => {})
    }

    const confirmSignUp = () => {
        setIsLoading(true)
        Auth.confirmSignUp(email, code)
            .then(() => {
                setCookie('userEmail', email, { path: '/' })
                if (redirectUrl) {
                    const url = new URL(redirectUrl)
                    url.searchParams.set('privacyUuid', privacyUUID)
                    url.searchParams.set('termsUuid', termsOfServiceUUID)
                    window.location.replace(url.toString())
                } else {
                    logIn()
                }
                setWaitingForCode(false)
                setAccountExists(false)
                setPasswordNotSafe(false)
                document.querySelector('body').scrollTo(0, 0)
            })
            .catch((err) => {
                setIsLoading(false)
                if (err.code === 'CodeMismatchException') setInvalidCode(true)
            })
    }

    const signUp = () => {
        setInviteExpired(false)
        setInvalidEmail(false)
        setInvalidPhone(false)
        clearErrors()
        setIsLoading(true)
        Auth.signUp({
            username: email,
            password,
            attributes: {
                email,
                name,
                family_name: lastName,
                phone_number: '+1' + phoneNumber.replace(/-/g, ''),
                'custom:propertyUuid': cookies?.propertyUuid,
                'custom:inviteUuid': cookies?.inviteUuid,
            },
        })
            .then(() => {
                setWaitingForCode(true)
                setIsLoading(false)
                document.querySelector('body').scrollTo(0, 0)
            })
            .catch((err) => {
                setIsLoading(false)
                if (err.code === 'UsernameExistsException') {
                    setAccountExists(true)
                    setPassword('')
                } else if (err.code === 'InvalidPasswordException') {
                    setAccountExists(false)
                    setPasswordNotSafe(true)
                } else if (err.toString().includes('Invalid email')) {
                    setInvalidEmail(true)
                } else if (err.toString().includes('Invalid phone')) {
                    setInvalidPhone(true)
                }
            })
    }

    useEffect(() => {
        setIsGuestCharging(props.isGuestCharging)
        if (pathId) {
            removeCookie('inviteUuid', { path: '/' })
            // The previous invite's role; this link's lookup sets its own.
            removeCookie('inviteRole', { path: '/' })
            setCookie('inviteUuid', pathId, { path: '/' })
        }
        if (expiration && isValidDateUnix(expiration)) {
            setGuestExpiration(formatDateUnix(expiration))
        }
    }, [props.isGuestCharging, pathId, expiration])

    useEffect(() => {
        async function getInviteDetails() {
            setIsLoading(true)
            setInviteChecked(false)
            try {
                const res = await fetch(API_URL_ADMIN + 'tools/invites/' + pathId, {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                })
                const result = await res.json()
                setEmail(result?.email)
                setRedirectUrl(result?.redirectUrl)
                setRole(result?.role)
                if (result?.code) setInviteExpired(true)
                // Sign-in reads it to land an invited admin or manager in their own app.
                else if (result?.role) setCookie('inviteRole', result.role, { path: '/' })
            } catch {
                setShowInviteError(true)
            } finally {
                setIsLoading(false)
                setInviteChecked(true)
            }
        }
        if (pathId === 'codeValidation') {
            resendCode()
            setInviteChecked(true)
        } else if (pathId?.length === 36) {
            getInviteDetails()
        } else {
            setInviteChecked(true)
        }
    }, [pathId])

    useEffect(() => {
        if (!inviteChecked) return
        setWaitingForCode(false)
        const legalDocumentsUrl = `${PUBLIC_URL}legal/en/documents?audience=${role}`
        fetch(legalDocumentsUrl, { method: 'GET' })
            .then((r) => r.json())
            .then((result) => {
                if (result) {
                    setPrivacyUUID(result?.privacyUUID)
                    setTermsOfServiceUUID(result?.termsOfServiceUUID)
                    fetch(result.privacyUrl).then((r) => r.text()).then(setPrivacyText)
                    fetch(result.termsOfServiceUrl).then((r) => r.text()).then(setTermsText)
                    setTermsLoading(false)
                }
            })
            .catch(() => setTermsLoading(false))
        document.querySelector('body').scrollTo(0, 0)
        if (cookies.userEmail && cookies.userConfirmed === 'false') {
            setEmail(cookies.userEmail)
            setWaitingForCode(true)
        }
    }, [inviteChecked])

    const canSubmit =
        password.length > 0 &&
        phoneNumber?.length > 0 &&
        name?.length > 0 &&
        termsAccepted &&
        lastName?.length > 0 &&
        email?.length > 0 &&
        !isLoading

    return (
        <>
            <TermsModal
                handleClose={() => setOpenTermsModal(false)}
                open={openTermsModal}
                setTermsAccepted={setTermsAccepted}
                termsText={termsText}
                privacyText={privacyText}
                loading={termsLoading}
                showActions
            />
            {isLoading && <Loader />}
            <GpAuthShell>
                <h2
                    data-testid="cypress-sign-up-header"
                    style={{
                        font: '500 32px/1.1 var(--gp-font)',
                        letterSpacing: -0.5,
                        margin: '0 0 6px',
                        color: 'var(--gp-ink)',
                    }}
                >
                    {waitingForCode ? 'Verify your email' : 'Sign up'}
                </h2>
                <p
                    style={{
                        font: 'var(--gp-body)',
                        color: 'var(--gp-ink-mute)',
                        margin: '0 0 24px',
                    }}
                >
                    {waitingForCode
                        ? <>A code has been sent to <strong>{email}</strong>. Enter it below to confirm.</>
                        : 'Create an account to continue.'}
                </p>

                {inviteExpired && (
                    <div
                        style={{
                            font: 'var(--gp-caption)',
                            color: 'var(--gp-danger)',
                            marginTop: 6,
                            marginBottom: 18,
                        }}
                    >
                        Invite is no longer active or doesn't exist. Please request a new invite.
                    </div>
                )}

                {showInviteError && (
                    <div
                        style={{
                            font: 'var(--gp-caption)',
                            color: 'var(--gp-danger)',
                            marginTop: 6,
                            marginBottom: 18,
                        }}
                    >
                        Could not look up that invite. Please try again.
                    </div>
                )}

                {waitingForCode && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <GpFormField
                            label="Verification code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="Enter 6-digit code"
                            inputMode="numeric"
                            autoFocus
                            inputStyle={{
                                font: '500 16px/1 var(--gp-mono)',
                                letterSpacing: 2,
                            }}
                            error={invalidCode ? 'Invalid code.' : null}
                        />

                        <GpButton
                            variant="primary"
                            onClick={confirmSignUp}
                            disabled={!code || isLoading}
                            style={{ width: '100%' }}
                        >
                            Confirm
                        </GpButton>

                        <div
                            style={{
                                font: 'var(--gp-caption)',
                                color: 'var(--gp-ink-mute)',
                                textAlign: 'center',
                            }}
                        >
                            Didn't get a code?{' '}
                            <span onClick={resendCode} style={linkStyle}>
                                Resend
                            </span>
                        </div>
                    </div>
                )}

                {!waitingForCode && !inviteExpired && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {isGuestCharging && cookies.inviteUuid && (
                            <div
                                style={{
                                    font: 'var(--gp-caption)',
                                    color: 'var(--gp-ink-mute)',
                                    marginBottom: 4,
                                }}
                            >
                                If you already have a Guest account,{' '}
                                <span onClick={goToLogin} style={linkStyle}>
                                    sign in instead
                                </span>
                                .
                            </div>
                        )}

                        <GpFormField
                            label="Email"
                            icon="mail"
                            type="email"
                            value={email}
                            onChange={(e) => !disabledEmail && setEmail(e.target.value)}
                            placeholder="you@example.com"
                            autoCapitalize="none"
                            disabled={disabledEmail}
                            data-testid="cypress-sign-up-email-field"
                            error={
                                accountExists
                                    ? 'You already have a GoPowerEV account with this email.'
                                    : invalidEmail
                                        ? 'Invalid email.'
                                        : null
                            }
                        />

                        {accountExists && (
                            // Cognito refuses a second account for an email, and every role an
                            // existing account can gain here (consumer access, an invite) is
                            // granted at sign-in. The invite cookie set above rides along.
                            <div
                                data-testid="cypress-sign-up-account-exists"
                                style={{
                                    font: 'var(--gp-caption)',
                                    color: 'var(--gp-ink-mute)',
                                    marginBottom: 4,
                                }}
                            >
                                <span onClick={goToLogin} style={linkStyle}>
                                    Sign in
                                </span>{' '}
                                to use it here
                                {cookies.inviteUuid ? ' — your invite comes with you.' : '.'}
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <GpFormField
                                label="First name"
                                icon="user"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your first name"
                                data-testid="cypress-sign-up-firstName-field"
                            />
                            <GpFormField
                                label="Last name"
                                icon="user"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Your last name"
                                data-testid="cypress-sign-up-lastName-field"
                            />
                        </div>

                        <InputMask
                            mask="999-999-9999"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            maskChar=" "
                        >
                            {() => (
                                <GpFormField
                                    label="Phone number"
                                    icon="phone"
                                    type="tel"
                                    placeholder="000-000-0000"
                                    data-testid="cypress-sign-up-phoneNumber-field"
                                    inputStyle={{
                                        font: '500 14px/1 var(--gp-mono)',
                                    }}
                                    error={invalidPhone ? 'Invalid phone number.' : null}
                                    helpText="We'll never call you or share your number."
                                />
                            )}
                        </InputMask>

                        <div>
                            <GpFormField
                                label="Create password"
                                icon="lock"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                data-testid="cypress-sign-up-password-field"
                                trailing={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((v) => !v)}
                                        aria-label={
                                            showPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                        data-testid="cypress-sign-up-showPassword-icon"
                                        style={iconBtn}
                                    >
                                        <Icon
                                            name={showPassword ? 'eyeOff' : 'eye'}
                                            size={18}
                                            stroke={1.75}
                                        />
                                    </button>
                                }
                                error={
                                    passwordNotSafe
                                        ? 'Password needs upper-case, number, and a symbol.'
                                        : null
                                }
                                helpText="At least 8 characters with a number and a symbol."
                            />
                            <PasswordStrengthBar
                                barColors={['#EDEBE4', '#E4A8A8', '#E5C77A', '#9FCFB1', '#005339']}
                                password={password}
                                minLength={6}
                            />
                        </div>

                        <label
                            style={{
                                marginTop: 4,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 10,
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={() => setTermsAccepted((v) => !v)}
                                data-testid="cypress-sign-up-termsCheckbox-field"
                                style={{ marginTop: 3, accentColor: 'var(--gp-brand)' }}
                            />
                            <span style={{ font: 'var(--gp-body)', color: 'var(--gp-ink)' }}>
                                I agree to the{' '}
                                <span onClick={() => setOpenTermsModal(true)} style={linkStyle}>
                                    Terms & Conditions
                                </span>
                            </span>
                        </label>

                        <div style={{ marginTop: 6 }}>
                            <GpButton
                                variant="primary"
                                onClick={signUp}
                                disabled={!canSubmit}
                                data-testid="cypress-sign-up-signUp-button"
                                style={{ width: '100%' }}
                            >
                                {isLoading ? 'Creating account…' : 'Create account'}
                            </GpButton>
                        </div>

                        <div
                            style={{
                                margin: '24px 0 0',
                                height: 1,
                                background:
                                    'repeating-linear-gradient(to right, var(--gp-hairline) 0, var(--gp-hairline) 4px, transparent 4px, transparent 8px)',
                            }}
                        />

                        <div
                            style={{
                                marginTop: 18,
                                font: 'var(--gp-body)',
                                color: 'var(--gp-ink-mute)',
                                textAlign: 'center',
                            }}
                        >
                            Already have an account?{' '}
                            <span onClick={goToLogin} style={linkStyle}>
                                Sign in
                            </span>
                        </div>
                    </div>
                )}
            </GpAuthShell>
        </>
    )
}

export default SignUp
